import React, { useState } from 'react';
import { useCoursePartnerList } from '@/hooks/queries/course-partner/useList';
import { useCoursePartnerCreate, useCoursePartnerUpdate, useCoursePartnerDelete } from '@/hooks/mutations/course-partner';
import { usePartnerList } from '@/hooks/queries/partner/useList';
import { CoursePartner, CoursePartnerInput } from '@/types/coursePartners';
import { Partner } from '@/types/partners';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, Handshake, ExternalLink } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { getMediaUrl } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

const CoursePartnersPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCP, setSelectedCP] = useState<CoursePartner | null>(null);

  const [formData, setFormData] = useState<CoursePartnerInput>({
    courseId: '',
    partnerId: '',
  });

  const { data: cpData, isLoading, refetch } = useCoursePartnerList({
    pageNumber: page,
    pageSize: 50,
  });

  const { data: partnersData } = usePartnerList({ pageNumber: 1, pageSize: 100 });
  const partnersList: Partner[] = partnersData?.data?.data || partnersData?.data || [];

  const { mutate: createCP, isPending: isCreating } = useCoursePartnerCreate();
  const { mutate: updateCP, isPending: isUpdating } = useCoursePartnerUpdate();
  const { mutate: deleteCP } = useCoursePartnerDelete();

  const handleOpenCreate = () => {
    setSelectedCP(null);
    setFormData({
      courseId: '',
      partnerId: partnersList[0]?.id || '',
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (cp: CoursePartner) => {
    setSelectedCP(cp);
    setFormData({
      courseId: cp.courseId || '',
      partnerId: cp.partnerId || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCP) {
      updateCP(
        { id: selectedCP.id, data: { partnerId: formData.partnerId } },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Hamkor yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Hamkor yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createCP(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Hamkor qo\'shildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Hamkor qo\'shilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedCP) return;
    deleteCP(selectedCP.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Hamkor o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedCP(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Hamkor o'chirilmadi", variant: 'destructive' }),
    });
  };

  const cpList: CoursePartner[] = cpData?.data?.data || cpData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Kurs Hamkorlari</h1>
          <p className="text-sm text-slate-500 mt-1">Rasmiy ish beruvchi kompaniyalar va hamkorlar ro'yxati</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi hamkor
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : cpList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Handshake className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha hamkorlar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Logotip</th>
                  <th className="px-4 py-3.5">Hamkor Nomi</th>
                  <th className="px-4 py-3.5">Tavsif</th>
                  <th className="px-4 py-3.5">Veb-sayt</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cpList.map((cp) => {
                  const logo = cp.logo || cp.partner?.logo;
                  const name = cp.name || cp.partner?.name || 'Hamkor';
                  const description = cp.description || cp.partner?.description;
                  const website = cp.website || cp.partner?.website;

                  return (
                    <tr key={cp.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        {logo ? (
                          <img
                            src={getMediaUrl(logo)}
                            alt={name}
                            className="h-9 w-auto max-w-[90px] object-contain rounded border border-slate-100 p-1 bg-white"
                          />
                        ) : (
                          <div className="size-9 rounded bg-slate-100 text-slate-400 flex items-center justify-center font-bold">
                            H
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{name}</td>
                      <td className="px-4 py-3 max-w-sm text-xs text-slate-500 line-clamp-2">
                        {description || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {website ? (
                          <a
                            href={website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline inline-flex items-center gap-1 font-medium"
                          >
                            Saytga o'tish <ExternalLink className="size-3" />
                          </a>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenEdit(cp)}
                            className="size-8 text-slate-600 hover:text-primary rounded-lg"
                          >
                            <Edit2 className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedCP(cp);
                              setIsDeleteOpen(true);
                            }}
                            className="size-8 text-slate-600 hover:text-red-500 rounded-lg"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={cpData?.meta?.pagination ?? cpData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedCP ? 'Hamkorni tahrirlash' : 'Yangi hamkor qo\'shish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Hamkor kompaniya</Label>
              <select
                value={formData.partnerId}
                onChange={(e) => setFormData({ ...formData, partnerId: e.target.value })}
                required
                className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="" disabled>Hamkorni tanlang...</option>
                {partnersList.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Hamkorni o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu hamkor o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default CoursePartnersPage;
